/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import { AlertType, Button } from '@toggle/design-system';
import { Entity, EntityResolved } from '@toggle/toggle';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BlockerFunction,
  useBlocker,
  useNavigate,
  useParams,
} from 'react-router';

import { useAssetsFilterEntityTags } from '~/hooks/use-assets-filter-entity-tags/useAssetsFilterEntityTags';
import { useRowsSelection } from '~/hooks/use-rows-selection/useRowsSelection';
import { useWatchlistActions } from '~/hooks/use-watchlist-actions/useWatchlistActions';
import { appPaths } from '~/routes/app-paths';
import { usePosthogEvents } from '~/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import {
  FormattedResolvedEntityV3Data,
  resolveEntitiesFromCsvV3,
} from '~/stores/use-watchlist/entityResolver';
import { useWatchlist } from '~/stores/use-watchlist/useWatchlist';
import { WatchlistError } from '~/stores/use-watchlist/watchlist-types';
import { useWatchlistManagerWidget } from '~/stores/use-watchlist-manager-widget/useWatchlistManagerWidget';
import { AlertMessage } from '~/views/onboarding/alert-message/AlertMessage';
import { useAlertMessage } from '~/views/onboarding/hooks/useAlertMessage';
import { useOnboardingStore } from '~/views/onboarding/hooks/useOnboardingStore';
import {
  isNewEntity,
  mergeDuplicateTickers,
  mergeUnMappedEntitiesLists,
  splitAllResolvedEntities,
} from '~/views/onboarding/utils/utils';

import { ChatWatchlistManager } from '../chat-watchlist-manager-widget/ChatWatchlistManagerWidget';
import { TableWatchlistManagerWidget } from '../table-watchlist-manager-widget/TableWatchlistManagerWidget';
import { AssetFilter } from './components/asset-filter/AssetFilter';
import { ConfirmationModal } from './components/confirmation-modal/ConfirmationModal';
import { WatchlistManagerHeader } from './components/watchlist-manager-header/WatchlistManagerHeader';
import { useEntityChangeHandlers } from './useEntityChangeHandlers';
import * as S from './WatchlistManagerWidget.styles';

export interface WatchlistManagerWidgetProps {
  onListCreated: (id: string) => void;
  onCancel?: () => void;
  className?: string;
}

export const WatchlistManagerWidget = ({
  className,
  onListCreated,
  onCancel,
}: WatchlistManagerWidgetProps) => {
  const { t } = useTranslation(['watchlistManager', 'common']);
  const navigate = useNavigate();
  const unMappedRowRef = useRef<HTMLTableRowElement>(null);

  const [countEntitiesAdded, setCountEntitiesAdded] = useState(0);
  const [isPending, setIsPending] = useState(false);
  const [isRenameActive, setIsRenameActive] = useState(false);
  const [listName, setListName] = useState('');
  const [savingInProgress, setSavingInProgress] = useState(false);
  const [chatInputState, setChatInputState] = useState({
    value: '',
    isAssetMessage: true,
  });
  const [previousEntityTagsFromFilter, setPreviousEntityTagsFromFilter] =
    useState<string[]>([]);
  const shadowContainerRef = useRef<HTMLDivElement>(null);
  const { watchlistId } = useParams();
  const { lists, updateList, filterGroups, resetFilterGroups } = useWatchlist(
    state => ({
      lists: state.lists,
      updateList: state.updateList,
      filterGroups: state.filterGroups,
      resetFilterGroups: state.resetFilterGroups,
    })
  );
  const { trackWatchlistCreate } = usePosthogEvents();

  const { data: entityTagsData, isLoading: isEntityTagsLoading } =
    useAssetsFilterEntityTags(filterGroups);

  const {
    entitiesList,
    unMappedEntitiesList,
    resolvedEntityData,
    allDuplicateTickers,
    setResolvedEntityData,
    setEntitiesList,
    setUnMappedEntitiesList,
    clearStore,
    setAllDuplicateTickers,
  } = useOnboardingStore(state => ({
    entitiesList: state.entitiesList,
    unMappedEntitiesList: state.unMappedEntitiesList,
    resolvedEntityData: state.resolvedEntityData,
    allDuplicateTickers: state.allDuplicateTickers,
    setResolvedEntityData: state.setResolvedEntityData,
    setEntitiesList: state.setEntitiesList,
    setUnMappedEntitiesList: state.setUnMappedEntitiesList,
    clearStore: state.clearStore,
    setAllDuplicateTickers: state.setAllDuplicateTickers,
  }));

  const { alertMessage, setMessage, setIsError } = useAlertMessage({
    resolvedEntityData,
    allDuplicateTickers,
  });

  const { isAddMode, onConfirmCallback, shouldResetList } =
    useWatchlistManagerWidget(state => ({
      isAddMode: state.isAddMode,
      onConfirmCallback: state.onConfirmCallback,
      shouldResetList: state.shouldResetList,
    }));

  const {
    watchlistError,
    watchlistErrorText,
    createListLoading,
    hasExceededUsageLimit,
    createNewList,
    setWatchlistError,
  } = useWatchlistActions();

  const {
    rowSelection,
    setRowSelection,
    updateRowSelection,
    toggleAllRowsSelection,
  } = useRowsSelection(entitiesList);

  useEffect(() => setMessage(''), []);

  useEffect(() => {
    return () => {
      clearStore();
      resetFilterGroups();
    };
  }, []);

  // Entity tags from asset filter
  useEffect(() => {
    if (entityTagsData.length < previousEntityTagsFromFilter.length) {
      const toRemoveTagsFromFilter = previousEntityTagsFromFilter.filter(
        tag => !entityTagsData.includes(tag)
      );

      const newEntitiesList = entitiesList.filter(
        e => !toRemoveTagsFromFilter.includes(e.tag)
      );
      setEntitiesList(newEntitiesList);
      setUnMappedEntitiesList([]);
      setPreviousEntityTagsFromFilter(entityTagsData);
      return;
    }

    const newEntityTagsFromFilter = entityTagsData.filter(
      tag => !previousEntityTagsFromFilter.includes(tag)
    );

    if (!isEntityTagsLoading && newEntityTagsFromFilter.length > 0) {
      const chatInputValue = [newEntityTagsFromFilter.map(e => e.split('_')[0])]
        .join(',')
        .trim();

      setPreviousEntityTagsFromFilter(entityTagsData);
      onChatSubmit(chatInputValue);
    }
  }, [isEntityTagsLoading, entityTagsData.length]);

  const entityChangeHandlers = useEntityChangeHandlers(setRowSelection);

  const onUpdateName = (name: string) => {
    setListName(name);

    if (name !== listName) {
      setWatchlistError(undefined);
    }
  };

  const updateEntitiesList = ({
    entitiesList,
    newEntities,
  }: {
    entitiesList: EntityResolved[];
    newEntities?: EntityResolved[];
  }) => {
    setEntitiesList([...(newEntities ?? []), ...entitiesList]);

    const updatedRowSelection = { ...rowSelection };
    newEntities?.forEach(e => {
      updatedRowSelection[e.tag] = true;
    });
    setRowSelection(updatedRowSelection);
  };

  const onSelectItem = (entity: Entity, isInWatchlist: boolean) => {
    const entityResolved = {
      ...entity,
      input: '',
    };

    setWatchlistError(undefined);

    if (isInWatchlist) {
      removeEntity(entityResolved);
      setWatchlistError(undefined);
    } else {
      setCountEntitiesAdded(1);
      updateEntitiesList({ newEntities: [entityResolved], entitiesList });
      setTimeout(() => setCountEntitiesAdded(0), 1000);
    }
  };

  const handleEntities = (result: FormattedResolvedEntityV3Data) => {
    setResolvedEntityData(result);
    const {
      mappedEntities,
      unMappedEntitiesList: searchUnMappedEntities,
      tableDuplicates,
    } = splitAllResolvedEntities(
      result.entities.map(e => e.primaryEntity),
      entitiesList
    );
    const updatedUnmappedEntities = mergeUnMappedEntitiesLists(
      unMappedEntitiesList,
      searchUnMappedEntities
    );
    setUnMappedEntitiesList(updatedUnmappedEntities);

    const duplicates = mergeDuplicateTickers(
      result.duplicates,
      tableDuplicates
    );
    setAllDuplicateTickers(duplicates);

    const newEntities = mappedEntities.filter(r =>
      isNewEntity({ tag: r.tag, entitiesList })
    ) as EntityResolved[];

    setCountEntitiesAdded(newEntities.length);
    updateEntitiesList({ newEntities, entitiesList });
  };

  const onChatSubmit = async (value: string) => {
    setIsPending(true);
    setWatchlistError(undefined);
    const result = await resolveEntitiesFromCsvV3(value);
    handleEntities(result);
    setIsPending(false);
    setTimeout(() => setCountEntitiesAdded(0), 1000);
  };

  const onConfirmSuccess = (id: string) => {
    setListName('');
    updateEntitiesList({ entitiesList: [] });
    onListCreated(id);
  };

  const activeListInAddMode =
    isAddMode && watchlistId
      ? lists.find(list => list.id === watchlistId)
      : undefined;

  const activeTags = shouldResetList
    ? []
    : activeListInAddMode?.entities.map(e => e.tag) || [];

  const validateListCreation = () => {
    if (!isAddMode && !listName) {
      setWatchlistError(WatchlistError.EmptyName);
      setIsRenameActive(true);
      setSavingInProgress(false);
      return { isValid: false, tags: [], unresolvedTags: [] };
    }

    const tags = Object.keys(rowSelection).filter(tag => rowSelection[tag]);

    if (!isAddMode && !tags.length) {
      setWatchlistError(WatchlistError.EmptyList);
      setSavingInProgress(false);
      return { isValid: false, tags: [] };
    }

    return {
      isValid: true,
      tags,
      unresolvedTags: unMappedEntitiesList.map(e => e.input),
    };
  };

  const onConfirm = async (
    e?: React.MouseEvent<HTMLButtonElement>
  ): Promise<string | null> => {
    e?.stopPropagation();
    setSavingInProgress(true);

    const { isValid, tags, unresolvedTags } = validateListCreation();

    if (!isValid) {
      return null;
    }

    if (activeListInAddMode && watchlistId) {
      await updateList({
        id: watchlistId,
        name: activeListInAddMode.name,
        tags: [...tags, ...activeTags],
      });
      onConfirmCallback?.();

      return watchlistId;
    }

    const listId = await createNewList(listName, tags, unresolvedTags);
    setSavingInProgress(false);

    if (listId) {
      trackWatchlistCreate('watchlist');
    }

    return listId;
  };

  const removeEntity = (entity: EntityResolved) => {
    updateEntitiesList({
      entitiesList: entitiesList.filter(d => d.tag !== entity.tag),
    });
  };

  const getIsInWatchlist = (item: Entity) =>
    entitiesList.some(d => d.tag === item.tag);

  /**
   * Handles the confirmation process and navigation blocking.
   * If successful, proceeds with navigation; otherwise, resets the blocker.
   */
  const confirmAndProceed = async () => {
    const succeed = await onConfirm();

    if (succeed) {
      blocker.proceed?.();
    } else {
      blocker.reset?.();
    }
  };

  const onChatInputValueChange = (value: string, isAssetMessage = false) => {
    setChatInputState({ value, isAssetMessage });
  };

  const onSave = async () => {
    const newId = await onConfirm();

    if (newId) {
      onConfirmSuccess(newId);
    }
  };

  const clearAlertMessage = () => {
    setMessage('');
    setIsError(false);
  };

  const onToggleAllRows = (selected: boolean) => {
    setWatchlistError(undefined);
    toggleAllRowsSelection(selected);
  };

  const onRowSelectionChange = (id: string, isSelected: boolean) => {
    setWatchlistError(undefined);
    updateRowSelection(id, isSelected);
  };

  const hasUnsavedChanges = !!listName || !!entitiesList.length;
  /** savingInProgress prevents blocking navigation while a save operation is in progress*/
  const shouldBlock = useCallback<BlockerFunction>(
    () => !savingInProgress && hasUnsavedChanges && !hasExceededUsageLimit,
    [savingInProgress, hasUnsavedChanges, hasExceededUsageLimit]
  );
  const blocker = useBlocker(shouldBlock);

  const hasEntities = !!entitiesList.length;
  const selectedEntitiesCount = Object.values(rowSelection).filter(
    x => !!x
  ).length;
  const showActionButtons =
    hasEntities && (chatInputState.isAssetMessage || !chatInputState.value);

  const isEntitiesOverflowing =
    !!shadowContainerRef.current &&
    shadowContainerRef.current?.scrollHeight >
      shadowContainerRef.current?.clientHeight;

  return (
    <S.Container className={className} data-testid="watchlist-manager-widget">
      <AlertMessage
        tableRef={unMappedRowRef}
        alertMessage={alertMessage}
        clearMessage={clearAlertMessage}
        isEntitiesOverflowing={isEntitiesOverflowing}
        insideModal
      />
      <WatchlistManagerHeader
        listName={listName}
        onUpdateName={onUpdateName}
        isRenameActive={isRenameActive}
        setIsRenameActive={setIsRenameActive}
        hasEntities={hasEntities}
        hasInputError={!!watchlistErrorText}
        activeListName={activeListInAddMode?.name}
      />
      <div>
        <AssetFilter isPending={isPending} />
      </div>
      <S.Separator>
        <S.SeparatorLine />
        <S.SeparatorText>{t('common:or')}</S.SeparatorText>
        <S.SeparatorLine />
      </S.Separator>
      <S.ContentContainer>
        <TableWatchlistManagerWidget
          data={entitiesList}
          countEntitiesAdded={countEntitiesAdded}
          toggleAllRowsSelection={onToggleAllRows}
          rowSelection={rowSelection}
          updateRowSelection={onRowSelectionChange}
          shadowContainerRef={shadowContainerRef}
          unMappedRowRef={unMappedRowRef}
          isEmptyListError={watchlistError === WatchlistError.EmptyList}
          handlers={entityChangeHandlers}
          showIndustryColumn
          isPending={isPending}
        />
        {!hasExceededUsageLimit && (
          <>
            {hasEntities && (
              <S.Label>{t('watchlistManager:wantAddMore')}</S.Label>
            )}
            <ChatWatchlistManager
              value={chatInputState.value}
              isPending={isPending}
              getIsInWatchlist={getIsInWatchlist}
              onSelectItem={onSelectItem}
              onChatSubmit={onChatSubmit}
              onChange={onChatInputValueChange}
              activeListEntityTags={activeTags}
            />
          </>
        )}
      </S.ContentContainer>
      {hasExceededUsageLimit && (
        <S.StyledAlert
          variant="inline"
          type={AlertType.Warning}
          title={t('watchlistManager:watchlistLimitReached')}
          message={t('watchlistManager:upgradeToCreateMore')}
          primaryBtn={{
            variant: 'primary',
            onClick: () => {
              navigate(appPaths.settings.subscription);
            },
            label: t('watchlistManager:upgrade'),
            size: 'large',
          }}
        />
      )}
      {showActionButtons && (
        <S.FooterContainer>
          {watchlistErrorText && (
            <>
              <S.StyledIcon
                size={20}
                iconName="ExclamationCircle"
                fillColor="var(--icon-critical)"
              />
              <S.ErrorMessage data-testid="error-message">
                {watchlistErrorText}
              </S.ErrorMessage>
            </>
          )}
          <S.FlexContainer>
            {onCancel && (
              <Button
                label={t('common:cancel')}
                variant="tertiary"
                size="large"
                onClick={onCancel}
              />
            )}
            <Button
              label={
                activeListInAddMode
                  ? t(
                      'watchlistManager:confirmationModal.addAssetsToWatchlist',
                      {
                        count: selectedEntitiesCount,
                      }
                    )
                  : t('watchlistManager:confirmationModal.save')
              }
              variant="primary"
              size="large"
              onClick={onSave}
              isLoading={createListLoading}
              disabled={createListLoading || hasExceededUsageLimit}
              data-testid="watchlist-manager-confirm-button"
            />
          </S.FlexContainer>
        </S.FooterContainer>
      )}
      {blocker.state === 'blocked' && (
        <ConfirmationModal
          onCancel={blocker.proceed}
          onClose={blocker.reset}
          onConfirm={confirmAndProceed}
          hasActiveListInAddMode={!!activeListInAddMode}
          selectedEntitiesCount={selectedEntitiesCount}
        />
      )}
    </S.Container>
  );
};
